/**
 * Price Range Filter Component
 * Dual-handle range slider for filtering by price
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import clsx from 'clsx'

// Predefined price breakpoints (in VND)
const PRICE_BREAKPOINTS = [
  0,
  500000000,    // 500 triệu
  1000000000,   // 1 tỷ
  2000000000,   // 2 tỷ
  3000000000,   // 3 tỷ
  5000000000,   // 5 tỷ
  7000000000,   // 7 tỷ
  10000000000,  // 10 tỷ
  15000000000,  // 15 tỷ
  20000000000,  // 20 tỷ
  30000000000,  // 30 tỷ
  50000000000,  // 50 tỷ
]

// Format price for display
const formatPriceShort = (value) => {
  if (value >= 1000000000) {
    const ty = value / 1000000000
    return ty % 1 === 0 ? `${ty} tỷ` : `${ty.toFixed(1)} tỷ`
  }
  if (value >= 1000000) {
    const trieu = value / 1000000
    return `${Math.round(trieu)} tr`
  }
  return '0'
}

function PriceRangeFilter({
  minPrice = 0,
  maxPrice = 50000000000,
  onChange,
  markers = [],
}) {
  // Find appropriate range based on marker data
  const dataRange = useMemo(() => {
    const prices = markers
      .filter(m => m.price_value)
      .map(m => m.price_value)

    if (prices.length === 0) {
      return { min: 0, max: PRICE_BREAKPOINTS[PRICE_BREAKPOINTS.length - 1] }
    }

    const dataMin = Math.min(...prices)
    const dataMax = Math.max(...prices)

    // Find breakpoint indices
    let minIdx = PRICE_BREAKPOINTS.findIndex(p => p >= dataMin)
    let maxIdx = PRICE_BREAKPOINTS.findIndex(p => p >= dataMax)

    if (minIdx > 0) minIdx -= 1
    if (maxIdx === -1) maxIdx = PRICE_BREAKPOINTS.length - 1
    else if (maxIdx < PRICE_BREAKPOINTS.length - 1) maxIdx += 1

    return {
      min: PRICE_BREAKPOINTS[minIdx],
      max: PRICE_BREAKPOINTS[maxIdx],
      minIdx,
      maxIdx,
    }
  }, [markers])

  // State for slider positions (0-100)
  const [minPos, setMinPos] = useState(0)
  const [maxPos, setMaxPos] = useState(100)
  const [isDragging, setIsDragging] = useState(null) // 'min' | 'max' | null

  // Convert position to price using breakpoints
  const posToPrice = useCallback((pos) => {
    const range = dataRange.maxIdx - dataRange.minIdx
    const idx = dataRange.minIdx + Math.round((pos / 100) * range)
    return PRICE_BREAKPOINTS[Math.min(idx, PRICE_BREAKPOINTS.length - 1)]
  }, [dataRange])

  // Convert price to position
  const priceToPos = useCallback((price) => {
    let idx = PRICE_BREAKPOINTS.findIndex(p => p >= price)
    if (idx === -1) idx = PRICE_BREAKPOINTS.length - 1

    const range = dataRange.maxIdx - dataRange.minIdx
    if (range === 0) return 0

    return ((idx - dataRange.minIdx) / range) * 100
  }, [dataRange])

  // Initialize positions from props
  useEffect(() => {
    setMinPos(priceToPos(minPrice))
    setMaxPos(priceToPos(maxPrice))
  }, [minPrice, maxPrice, priceToPos])

  // Current values
  const currentMin = posToPrice(minPos)
  const currentMax = posToPrice(maxPos)

  // Handle slider change
  const handleMinChange = (newPos) => {
    const clampedPos = Math.min(newPos, maxPos - 5)
    setMinPos(Math.max(0, clampedPos))
  }

  const handleMaxChange = (newPos) => {
    const clampedPos = Math.max(newPos, minPos + 5)
    setMaxPos(Math.min(100, clampedPos))
  }

  // Notify parent on change end
  const handleChangeEnd = useCallback(() => {
    if (onChange) {
      onChange({
        min: posToPrice(minPos),
        max: posToPrice(maxPos),
      })
    }
    setIsDragging(null)
  }, [onChange, minPos, maxPos, posToPrice])

  // Quick select buttons
  const quickSelects = [
    { label: 'Tất cả', min: 0, max: PRICE_BREAKPOINTS[PRICE_BREAKPOINTS.length - 1] },
    { label: '< 2 tỷ', min: 0, max: 2000000000 },
    { label: '2-5 tỷ', min: 2000000000, max: 5000000000 },
    { label: '5-10 tỷ', min: 5000000000, max: 10000000000 },
    { label: '> 10 tỷ', min: 10000000000, max: PRICE_BREAKPOINTS[PRICE_BREAKPOINTS.length - 1] },
  ]

  const handleQuickSelect = (item) => {
    setMinPos(priceToPos(item.min))
    setMaxPos(priceToPos(item.max))
    if (onChange) {
      onChange({ min: item.min, max: item.max })
    }
  }

  // Check if a quick select is active
  const isQuickSelectActive = (item) => {
    return currentMin === item.min && currentMax === item.max
  }

  return (
    <div className="space-y-3">
      {/* Quick Select Buttons */}
      <div className="flex flex-wrap gap-1">
        {quickSelects.map((item) => (
          <button
            key={item.label}
            onClick={() => handleQuickSelect(item)}
            className={clsx(
              'px-2 py-1 text-xs rounded-md transition-colors',
              isQuickSelectActive(item)
                ? 'bg-primary-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Range Slider */}
      <div className="relative h-6 px-2">
        {/* Track background */}
        <div className="absolute top-1/2 left-2 right-2 h-1 -translate-y-1/2 bg-gray-700 rounded-full" />

        {/* Active track */}
        <div
          className="absolute top-1/2 h-1 -translate-y-1/2 bg-primary-500 rounded-full"
          style={{
            left: `calc(${minPos}% + 8px - ${minPos * 0.16}px)`,
            right: `calc(${100 - maxPos}% + 8px - ${(100 - maxPos) * 0.16}px)`,
          }}
        />

        {/* Min handle */}
        <input
          type="range"
          min="0"
          max="100"
          value={minPos}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          onMouseDown={() => setIsDragging('min')}
          onMouseUp={handleChangeEnd}
          onTouchEnd={handleChangeEnd}
          className="absolute w-full h-full opacity-0 cursor-pointer pointer-events-auto z-20"
          style={{
            clipPath: `inset(0 ${100 - (minPos + maxPos) / 2}% 0 0)`,
          }}
        />

        {/* Max handle */}
        <input
          type="range"
          min="0"
          max="100"
          value={maxPos}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          onMouseDown={() => setIsDragging('max')}
          onMouseUp={handleChangeEnd}
          onTouchEnd={handleChangeEnd}
          className="absolute w-full h-full opacity-0 cursor-pointer pointer-events-auto z-20"
          style={{
            clipPath: `inset(0 0 0 ${(minPos + maxPos) / 2}%)`,
          }}
        />

        {/* Min thumb visual */}
        <div
          className={clsx(
            'absolute top-1/2 w-4 h-4 -translate-y-1/2 -translate-x-1/2 rounded-full border-2 transition-all z-10',
            isDragging === 'min'
              ? 'bg-primary-400 border-primary-400 scale-125'
              : 'bg-white border-primary-500'
          )}
          style={{ left: `${minPos}%` }}
        />

        {/* Max thumb visual */}
        <div
          className={clsx(
            'absolute top-1/2 w-4 h-4 -translate-y-1/2 -translate-x-1/2 rounded-full border-2 transition-all z-10',
            isDragging === 'max'
              ? 'bg-primary-400 border-primary-400 scale-125'
              : 'bg-white border-primary-500'
          )}
          style={{ left: `${maxPos}%` }}
        />
      </div>

      {/* Current Range Display */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-primary-400 font-medium">
          {formatPriceShort(currentMin)}
        </span>
        <span className="text-gray-500">—</span>
        <span className="text-primary-400 font-medium">
          {currentMax >= PRICE_BREAKPOINTS[PRICE_BREAKPOINTS.length - 1]
            ? '50+ tỷ'
            : formatPriceShort(currentMax)}
        </span>
      </div>
    </div>
  )
}

export default PriceRangeFilter
